'use strict';

const PendingChoiceType = Object.freeze({
  ASSIGN_COMBAT_DAMAGE: 'assign_combat_damage',
  CHAIN_PRIORITY: 'chain_priority',
  CHOOSE_BATTLEFIELD: 'choose_battlefield',
  // @deprecated — never emitted by the engine. Use CHOOSE_TARGET with
  // battlefield-filtered candidates instead.
  CHOOSE_BATTLEFIELD_UNIT: 'choose_battlefield_unit',
  CHOOSE_CARD_FROM_HAND: 'choose_card_from_hand',
  CHOOSE_CARD_FROM_ZONE: 'choose_card_from_zone',
  CHOOSE_CARDS_FROM_ZONE: 'choose_cards_from_zone',
  CHOOSE_COMBAT_ORDER: 'choose_combat_order',
  CHOOSE_ONE: 'choose_one',
  CHOOSE_PLAYER: 'choose_player',
  CHOOSE_REVEALED_CARD: 'choose_revealed_card',
  CHOOSE_RUNES: 'choose_runes',
  CHOOSE_TARGET: 'choose_target',
  CHOOSE_UNIT_AND_SPELL: 'choose_unit_and_spell',
  CHOOSE_UNIT_PAIR: 'choose_unit_pair',
  CHOOSE_UNITS: 'choose_units',
  DISCARD: 'discard',
  DEATH_REPLACEMENT: 'death_replacement',
  ORDER_TRIGGERS: 'order_triggers',
  OPTIONAL_COST: 'optional_cost',
  PREDICT: 'predict',
  REPEAT: 'repeat',
  TOKEN_COPY: 'token_copy',
  VISION: 'vision',
  WEAPONMASTER: 'weaponmaster',
});

const PendingChoiceMeta = Object.freeze({
  [PendingChoiceType.ASSIGN_COMBAT_DAMAGE]: {
    actor: 'player',
    resolveAction: 'submit_damage_assignment',
    payloadKeys: ['assignment'],
    frontendComponent: 'DamageAssignmentOverlay',
  },
  [PendingChoiceType.CHAIN_PRIORITY]: {
    actor: 'priorityPlayer',
    resolveAction: 'pass_chain_priority',
    payloadKeys: [],
    frontendComponent: 'ChainPriorityBanner',
  },
  [PendingChoiceType.CHOOSE_BATTLEFIELD]: {
    actor: 'player',
    resolveAction: 'resolve_choose_battlefield',
    payloadKeys: ['battlefieldId'],
    frontendComponent: 'ChooseBattlefieldOverlay',
  },
  // @deprecated — unused by the engine. Use CHOOSE_TARGET with
  // battlefield-filtered candidates instead.
  [PendingChoiceType.CHOOSE_BATTLEFIELD_UNIT]: {
    actor: 'player',
    resolveAction: 'resolve_choose_battlefield_unit',
    payloadKeys: ['unitId'],
    frontendComponent: 'ChooseBattlefieldUnitOverlay',
  },
  [PendingChoiceType.CHOOSE_CARD_FROM_HAND]: {
    actor: 'player',
    resolveAction: 'resolve_choose_card_from_hand',
    payloadKeys: ['cardId'],
    frontendComponent: 'ChooseCardFromHandOverlay',
  },
  [PendingChoiceType.CHOOSE_CARD_FROM_ZONE]: {
    actor: 'player',
    resolveAction: 'resolve_choose_card_from_zone',
    payloadKeys: ['cardId'],
    frontendComponent: 'ChooseCardFromZoneOverlay',
  },
  [PendingChoiceType.CHOOSE_CARDS_FROM_ZONE]: {
    actor: 'player',
    resolveAction: 'resolve_choose_cards_from_zone',
    payloadKeys: ['cardIds'],
    frontendComponent: 'ChooseCardsFromZoneOverlay',
  },
  [PendingChoiceType.CHOOSE_COMBAT_ORDER]: {
    actor: 'currentPlayer',
    resolveAction: 'choose_combat_order',
    payloadKeys: ['battlefieldId'],
    frontendComponent: 'CombatOrderPicker',
  },
  [PendingChoiceType.CHOOSE_ONE]: {
    actor: 'player',
    resolveAction: 'resolve_choose_one',
    payloadKeys: ['choiceIndex'],
    frontendComponent: 'ChooseOneOverlay',
  },
  [PendingChoiceType.CHOOSE_PLAYER]: {
    actor: 'player',
    resolveAction: 'resolve_choose_player',
    payloadKeys: ['playerId'],
    frontendComponent: 'ChoosePlayerOverlay',
  },
  [PendingChoiceType.CHOOSE_REVEALED_CARD]: {
    actor: 'player',
    resolveAction: 'resolve_choose_revealed_card',
    payloadKeys: ['cardId'],
    frontendComponent: 'ChooseRevealedCardOverlay',
  },
  [PendingChoiceType.CHOOSE_RUNES]: {
    actor: 'player',
    resolveAction: 'resolve_choose_runes',
    payloadKeys: ['runeIds'],
    frontendComponent: 'ChooseRunesOverlay',
  },
  [PendingChoiceType.CHOOSE_TARGET]: {
    actor: 'player',
    resolveAction: 'resolve_choose_target',
    payloadKeys: ['unitId'],
    frontendComponent: 'ChooseTargetOverlay',
  },
  [PendingChoiceType.CHOOSE_UNIT_AND_SPELL]: {
    actor: 'player',
    resolveAction: 'resolve_choose_unit_and_spell',
    payloadKeys: ['unitId', 'spellId'],
    frontendComponent: 'ChooseUnitAndSpellOverlay',
  },
  [PendingChoiceType.CHOOSE_UNIT_PAIR]: {
    actor: 'player',
    resolveAction: 'resolve_choose_unit_pair',
    payloadKeys: ['unitIds'],
    frontendComponent: 'ChooseUnitPairOverlay',
  },
  [PendingChoiceType.CHOOSE_UNITS]: {
    actor: 'player',
    resolveAction: 'resolve_choose_units',
    payloadKeys: ['unitIds'],
    frontendComponent: 'ChooseUnitsOverlay',
  },
  [PendingChoiceType.DISCARD]: {
    actor: 'player',
    resolveAction: 'resolve_discard',
    payloadKeys: ['cardIds'],
    frontendComponent: 'DiscardOverlay',
  },
  [PendingChoiceType.DEATH_REPLACEMENT]: {
    actor: 'player',
    resolveAction: 'resolve_death_replacement',
    payloadKeys: ['accept', 'unitId', 'sourceId'],
    frontendComponent: 'DeathReplacementOverlay',
  },
  [PendingChoiceType.ORDER_TRIGGERS]: {
    actor: 'controller',
    resolveAction: 'resolve_order_triggers',
    payloadKeys: ['order', 'skip'],
    frontendComponent: 'TriggerOrderPicker',
  },
  [PendingChoiceType.OPTIONAL_COST]: {
    actor: 'player',
    resolveAction: 'resolve_optional_cost',
    payloadKeys: ['accept'],
    frontendComponent: 'OptionalCostOverlay',
  },
  [PendingChoiceType.PREDICT]: {
    actor: 'player',
    resolveAction: 'resolve_predict',
    payloadKeys: ['recycleIds', 'order'],
    frontendComponent: 'PredictOverlay',
  },
  [PendingChoiceType.REPEAT]: {
    actor: 'player',
    resolveAction: 'resolve_repeat',
    payloadKeys: ['repeat'],
    frontendComponent: 'RepeatOverlay',
  },
  [PendingChoiceType.TOKEN_COPY]: {
    actor: 'player',
    resolveAction: 'resolve_token_copy',
    payloadKeys: ['accept'],
    frontendComponent: 'TokenCopyOverlay',
  },
  [PendingChoiceType.VISION]: {
    actor: 'player',
    resolveAction: 'resolve_vision',
    payloadKeys: ['recycle'],
    frontendComponent: 'VisionOverlay',
  },
  [PendingChoiceType.WEAPONMASTER]: {
    actor: 'player',
    resolveAction: 'resolve_weaponmaster',
    payloadKeys: ['gearId'],
    frontendComponent: 'WeaponmasterOverlay',
  },
});

const PendingChoiceTypes = Object.freeze(Object.values(PendingChoiceType));

function isPendingChoiceType(type) {
  return PendingChoiceTypes.includes(type);
}

module.exports = {
  PendingChoiceType,
  PendingChoiceMeta,
  PendingChoiceTypes,
  isPendingChoiceType,
};
